import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, Languages } from "lucide-react";

export default function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);

  useEffect(() => { api.get("/admin/content").then(r => setContent(r.data?.data || {})); }, []);

  if (!content) return <div className="label-mono text-cyan-300">CARGANDO...</div>;

  const update = (path, val) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const updatePlan = (idx, key, val) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      next.plans[idx][key] = val;
      return next;
    });
  };

  const translate = async (sourceKey, targetKey, sourcePath, targetPath) => {
    const text = getByPath(content, sourcePath);
    if (!text) return toast.error("Texto vacío");
    setTranslating(targetPath);
    try {
      const { data } = await api.post("/admin/translate", { text, target: targetKey === "title_en" || targetKey.endsWith("_en") ? "en" : "es" });
      update(targetPath, data.translated);
      toast.success("Traducción aplicada");
    } catch (e) { toast.error("Error en traducción"); }
    finally { setTranslating(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { data: content });
      toast.success("Contenido publicado");
    } catch (e) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-content">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-1">/ Contenido</div>
          <h1 className="font-display text-4xl chrome-text">Editor de contenido</h1>
          <p className="text-white/60 mt-1 text-sm">Edita ES y EN. Usa el botón de traducción automática (Claude Sonnet 4.5) para acelerar.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-metal" data-testid="save-content"><Save className="w-4 h-4" /> {saving ? "..." : "Publicar"}</button>
      </header>

      <Section title="HERO">
        <BilingualField label="Título" esPath="hero.title_es" enPath="hero.title_en" content={content} update={update} translate={translate} translating={translating} multiline />
        <BilingualField label="Subtítulo" esPath="hero.subtitle_es" enPath="hero.subtitle_en" content={content} update={update} translate={translate} translating={translating} multiline />
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {content.hero.kpis.map((k, i) => (
            <div key={i} className="glass-card chrome-border p-4 space-y-2">
              <Input label={`KPI ${i+1} valor`} value={k.value} onChange={(v)=>update(`hero.kpis.${i}.value`, v)} testId={`kpi-value-${i}`} />
              <Input label="Label ES" value={k.label_es} onChange={(v)=>update(`hero.kpis.${i}.label_es`, v)} testId={`kpi-es-${i}`} />
              <Input label="Label EN" value={k.label_en} onChange={(v)=>update(`hero.kpis.${i}.label_en`, v)} testId={`kpi-en-${i}`} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="CONTACTO">
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="WhatsApp" value={content.contact.whatsapp} onChange={(v)=>update("contact.whatsapp", v)} testId="contact-whatsapp-input" />
          <Input label="Teléfono" value={content.contact.phone} onChange={(v)=>update("contact.phone", v)} testId="contact-phone-input" />
          <Input label="Email" value={content.contact.email} onChange={(v)=>update("contact.email", v)} testId="contact-email-input" />
          <div className="grid grid-cols-2 gap-2">
            <Input label="Dirección ES" value={content.contact.address_es} onChange={(v)=>update("contact.address_es", v)} testId="contact-addr-es" />
            <Input label="Dirección EN" value={content.contact.address_en} onChange={(v)=>update("contact.address_en", v)} testId="contact-addr-en" />
          </div>
        </div>
      </Section>

      <Section title="PLANES">
        {content.plans.map((p, idx) => (
          <div key={p.id} className="glass-card chrome-border p-5 mb-4">
            <div className="label-mono mb-3">Plan · {p.id}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Input label="Nombre ES" value={p.name_es} onChange={(v)=>updatePlan(idx, "name_es", v)} />
              <Input label="Nombre EN" value={p.name_en} onChange={(v)=>updatePlan(idx, "name_en", v)} />
              <Input label="Descripción ES" value={p.description_es} onChange={(v)=>updatePlan(idx, "description_es", v)} />
              <Input label="Descripción EN" value={p.description_en} onChange={(v)=>updatePlan(idx, "description_en", v)} />
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <TextArea label="Features ES (uno por línea)" value={(p.features_es||[]).join("\n")} onChange={(v)=>updatePlan(idx, "features_es", v.split("\n"))} />
              <TextArea label="Features EN (uno por línea)" value={(p.features_en||[]).join("\n")} onChange={(v)=>updatePlan(idx, "features_en", v.split("\n"))} />
            </div>
            <div className="mt-3">
              <label className="inline-flex items-center gap-2 text-sm text-white/70">
                <input type="checkbox" checked={!!p.highlight} onChange={(e)=>updatePlan(idx, "highlight", e.target.checked)} />
                Plan destacado (recomendado)
              </label>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

function getByPath(obj, path) {
  return path.split(".").reduce((c,k) => c?.[k], obj);
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <div className="label-mono mb-3">/ {title}</div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="label-mono">{label}</label>
      <input value={value || ""} onChange={(e)=>onChange(e.target.value)} data-testid={testId}
        className="mt-1 w-full rounded-lg bg-black/50 border border-white/10 px-3 py-2 text-white text-sm focus:border-cyan-400/50" />
    </div>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <div>
      <label className="label-mono">{label}</label>
      <textarea rows={4} value={value || ""} onChange={(e)=>onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-black/50 border border-white/10 px-3 py-2 text-white text-sm focus:border-cyan-400/50" />
    </div>
  );
}

function BilingualField({ label, esPath, enPath, content, update, translate, translating, multiline }) {
  const esVal = getByPath(content, esPath) || "";
  const enVal = getByPath(content, enPath) || "";
  const Field = multiline ? "textarea" : "input";
  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4">
      <div>
        <div className="flex items-center justify-between"><label className="label-mono">{label} · ES</label>
          <button type="button" onClick={()=>translate("es","en",esPath,enPath)} disabled={translating===enPath}
            className="text-xs label-mono !text-cyan-300 hover:!text-white inline-flex items-center gap-1"><Languages className="w-3 h-3" /> ES → EN</button>
        </div>
        <Field rows={3} value={esVal} onChange={(e)=>update(esPath, e.target.value)}
          className="mt-1 w-full rounded-lg bg-black/50 border border-white/10 px-3 py-2 text-white text-sm focus:border-cyan-400/50" />
      </div>
      <div>
        <div className="flex items-center justify-between"><label className="label-mono">{label} · EN</label>
          <button type="button" onClick={()=>translate("en","es",enPath,esPath)} disabled={translating===esPath}
            className="text-xs label-mono !text-cyan-300 hover:!text-white inline-flex items-center gap-1"><Languages className="w-3 h-3" /> EN → ES</button>
        </div>
        <Field rows={3} value={enVal} onChange={(e)=>update(enPath, e.target.value)}
          className="mt-1 w-full rounded-lg bg-black/50 border border-white/10 px-3 py-2 text-white text-sm focus:border-cyan-400/50" />
      </div>
    </div>
  );
}
